import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, Download, CheckCircle2 } from "lucide-react";

export default function HandoverPanel({ property, refreshKey }) {
  const [d, setD] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/properties/${property.id}/handover`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [property.id, refreshKey]); // eslint-disable-line

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/properties/${property.id}/handover.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `certy-handover-${property.name.replace(/\s+/g, "-")}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate pack"); } finally { setBusy(false); }
  };
  const sign = async () => {
    if (!name.trim()) return toast.error("Type the tenant's full name as their signature");
    try { await api.post(`/properties/${property.id}/handover/receipt`, { tenant_name: name, method: "e-signature in GetCerty" }); toast.success("Receipt recorded"); setName(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); }
  };
  if (!d) return null;
  const r = d.receipts[0];
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="handover-panel">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-navy text-white"><FileSignature size={20} /></span>
        <div className="flex-1">
          <p className="font-semibold">Tenant handover pack</p>
          <p className="text-xs text-muted-foreground">{d.items.length} document{d.items.length === 1 ? "" : "s"} marked as served{d.tenancy ? ` to ${d.tenancy.tenant_names}` : ""}. Proof of service for deposit disputes and notices.</p>
        </div>
        <button data-testid="download-handover" disabled={busy} onClick={download} className="min-h-[40px] px-3 inline-flex items-center gap-2 rounded-xl border border-border text-sm font-medium hover:bg-secondary disabled:opacity-60"><Download size={15} /> PDF</button>
      </div>
      <ul className="mt-3 text-sm space-y-1">
        {d.items.map((i, k) => <li key={k} className="flex justify-between border-b border-border py-1"><span>{i.name}</span><span className="text-muted-foreground text-xs">served {i.served_date}</span></li>)}
        {d.items.length === 0 && <li className="text-xs text-muted-foreground">Mark certificates and documents as "served" to build this pack.</li>}
      </ul>
      <div className="mt-4 p-3 rounded-xl bg-secondary/60">
        {r ? (
          <p className="text-sm flex items-start gap-2" data-testid="receipt-signed"><CheckCircle2 size={16} className="text-emerald-brand mt-0.5" /><span><b>{r.tenant_name}</b> acknowledged receipt of {r.items.length} documents on {r.signed_date} · record {r.id}</span></p>
        ) : (
          <>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tenant acknowledgement (e-signature)</p>
            <div className="flex gap-2">
              <input data-testid="receipt-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tenant types full name to sign" className="flex-1 min-h-[44px] px-3 rounded-xl border border-input bg-card" />
              <button data-testid="receipt-sign" onClick={sign} className="min-h-[44px] px-4 rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold">Sign</button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Hand the device to your tenant. Date, time and IP are recorded in the audit trail.</p>
          </>
        )}
      </div>
    </div>
  );
}
